import { useEffect, useRef } from 'react';

export const ANCHOR_SCROLL_MARGIN = '160px';

const FALLBACK_HEADER_OFFSET = 160;
const ACTIVE_STICKY_HEADER = '.sticky-header.active';
const CORRECTION_DELAY = 450;

type AnchorState = { ids: string[]; onMatch?: (anchorId: string) => void };
type AnchorRegistration = {
  getState: () => AnchorState;
  frames: Set<number>;
  timeouts: Set<number>;
};

const registrations = new Set<AnchorRegistration>();
let listenersInstalled = false;

const decodeHash = (rawHash: string): string => {
  try {
    return decodeURIComponent(rawHash);
  } catch {
    return rawHash;
  }
};

const getHashId = (): string =>
  typeof window === 'undefined' ? '' : decodeHash(window.location.hash.replace(/^#/, ''));

// Measures the live sticky header so the offset adapts across brands and breakpoints.
const getHeaderOffset = (): number => {
  if (typeof document === 'undefined') {
    return FALLBACK_HEADER_OFFSET;
  }
  const header = document.querySelector(ACTIVE_STICKY_HEADER);
  return header instanceof HTMLElement && header.offsetHeight ? header.offsetHeight : FALLBACK_HEADER_OFFSET;
};

const scheduleFrame = (registration: AnchorRegistration, callback: FrameRequestCallback): void => {
  const frame = window.requestAnimationFrame(timestamp => {
    registration.frames.delete(frame);
    callback(timestamp);
  });
  registration.frames.add(frame);
};

const scrollToTarget = (id: string, registration: AnchorRegistration): void => {
  const element = document.getElementById(id);
  if (!element) {
    return;
  }

  element.focus({ preventScroll: true });
  const scrollWithOffset = (): void => {
    const top = element.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
    window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
  };

  scheduleFrame(registration, () => scheduleFrame(registration, scrollWithOffset));
  const timeout = window.setTimeout(() => {
    registration.timeouts.delete(timeout);
    scrollWithOffset();
  }, CORRECTION_DELAY);
  registration.timeouts.add(timeout);
};

const handleAnchor = (id: string): void => {
  if (!id) {
    return;
  }

  for (const registration of registrations) {
    const { ids, onMatch } = registration.getState();
    if (ids.includes(id)) {
      onMatch?.(id);
      scrollToTarget(id, registration);
      return;
    }
  }
};

const handleHash = (): void => handleAnchor(getHashId());

const handleClick = (event: MouseEvent): void => {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return;
  }

  const { target } = event;
  const anchor = target instanceof Element ? (target.closest('a[href]') as HTMLAnchorElement | null) : null;
  if (!anchor || (anchor.target && anchor.target.toLowerCase() !== '_self') || anchor.hasAttribute('download')) {
    return;
  }

  const href = anchor.getAttribute('href') || '';
  if (!href.startsWith('#') || href.length < 2) {
    return;
  }

  const id = decodeHash(href.slice(1));
  if (![...registrations].some(registration => registration.getState().ids.includes(id))) {
    return;
  }

  event.preventDefault();
  if (window.location.hash !== `#${id}`) {
    window.history.pushState(null, '', `#${id}`);
  }
  handleAnchor(id);
};

const clearPendingScrolls = (registration: AnchorRegistration): void => {
  registration.frames.forEach(frame => window.cancelAnimationFrame(frame));
  registration.frames.clear();
  registration.timeouts.forEach(timeout => window.clearTimeout(timeout));
  registration.timeouts.clear();
};

const registerAnchorHandler = (registration: AnchorRegistration): (() => void) => {
  registrations.add(registration);
  if (!listenersInstalled) {
    window.addEventListener('hashchange', handleHash);
    document.addEventListener('click', handleClick);
    listenersInstalled = true;
  }
  handleHash();

  return () => {
    registrations.delete(registration);
    if (registrations.size === 0 && listenersInstalled) {
      window.removeEventListener('hashchange', handleHash);
      document.removeEventListener('click', handleClick);
      listenersInstalled = false;
    }
    clearPendingScrolls(registration);
  };
};

/**
 * Smoothly scrolls to an author-defined anchor target when the URL fragment matches —
 * on initial load, on `hashchange`, and on in-page anchor clicks (intercepted to avoid
 * the browser's instant jump). `onMatch` lets a consumer (e.g. an accordion) reveal the
 * target before it is scrolled into view.
 */
export const useAnchorScroll = (anchorIds?: string | string[], onMatch?: (anchorId: string) => void): void => {
  const stateRef = useRef<AnchorState>({ ids: [] });

  stateRef.current.ids = (Array.isArray(anchorIds) ? anchorIds : [anchorIds]).filter((id): id is string => Boolean(id));
  stateRef.current.onMatch = onMatch;

  useEffect(() => {
    if (stateRef.current.ids.length === 0) {
      return undefined;
    }
    return registerAnchorHandler({ getState: () => stateRef.current, frames: new Set(), timeouts: new Set() });
  }, [Array.isArray(anchorIds) ? anchorIds.join('\u0000') : anchorIds]);
};

export default useAnchorScroll;
